/**
 * ResolveControlBar — the pause / stop / resume strip for a bulk "Resolve
 * issues" pass. Rendered above the spine while a pass is in flight or paused.
 *
 * Separate from ``SpineControlBar`` (which controls the DRAFTING run) because a
 * resolve pass never enters the reducer's mid-run state — it is its own thing,
 * with its own lifecycle owned by ``useResolveIssuesFlow``. Pure presentation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import type { ResolvePhase, ResolveProgress } from "../hooks/useResolveIssuesFlow";

interface ResolveControlBarProps {
  phase: ResolvePhase;
  progress: ResolveProgress;
  remainingCount: number;
  onPause: () => void;
  onStop: () => void;
  onResume: () => void;
  onDiscard: () => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: "10px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    padding: "8px 12px",
    fontFamily: fonts.body,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: statusColors.hot,
    flexShrink: 0,
  },
  dotPaused: { background: statusColors.warn },
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
  },
  count: {
    fontSize: "11px",
    fontWeight: 400,
    color: inkColors.ink3,
    fontFamily: fonts.mono,
  },
  spacer: { flex: 1 },
  button: {
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    padding: "6px 10px",
    textTransform: "uppercase",
    transition: "all 150ms",
    "&:hover": { color: statusColors.warn },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  stopButton: { "&:hover": { color: statusColors.danger } },
  resumeButton: { "&:hover": { color: statusColors.cool } },
});

const ResolveControlBar: React.FC<ResolveControlBarProps> = ({
  phase,
  progress,
  remainingCount,
  onPause,
  onStop,
  onResume,
  onDiscard,
}) => {
  const styles = useStyles();
  if (phase === "idle") return null;

  const isPaused = phase === "paused";
  const busy = phase === "pausing" || phase === "stopping";
  const headline = isPaused
    ? "Review prep paused"
    : phase === "pausing"
      ? "Pausing…"
      : phase === "stopping"
        ? "Stopping…"
        : "Preparing changes to review";

  return (
    <div className={styles.root} role="status" aria-live="polite">
      <span className={mergeClasses(styles.dot, isPaused && styles.dotPaused)} aria-hidden />
      <span className={styles.label}>{headline}</span>
      {isPaused ? (
        <span className={styles.count}>
          {remainingCount} section{remainingCount === 1 ? "" : "s"} left
        </span>
      ) : (
        <span className={styles.count}>
          {progress.done}/{progress.total}
        </span>
      )}
      <span className={styles.spacer} />
      {isPaused ? (
        <>
          <button type="button" className={mergeClasses(styles.button, styles.resumeButton)} onClick={onResume}>
            Resume
          </button>
          <button type="button" className={styles.button} onClick={onDiscard}>
            Discard
          </button>
        </>
      ) : (
        <>
          <button type="button" className={styles.button} onClick={onPause} disabled={busy}>
            Pause
          </button>
          <button type="button" className={mergeClasses(styles.button, styles.stopButton)} onClick={onStop} disabled={busy}>
            Stop
          </button>
        </>
      )}
    </div>
  );
};

export default ResolveControlBar;
